<template>
  <section class="reviews-summary" :class="{ desktop: $isDesktop }">
    <div class="row first-row">
      <span>
        <pu-modal
          :use-portal="true"
          class="overall-rating"
          modal-hash="reviews-summary"
          @click.capture="handleClick"
        >
          <template #modal-trigger>
            <div>
              <div class="cell overall modal-trigger">
                <pu-rating-bubble
                  :rating="overallRating"
                  :rate-count="rateCount"
                  :force-visibility="true"
                  class="overall-rating"
                />
                <p>
                  <translate :tt="{ reviewsCount: rateCount }">
                    Overall rating from {reviewsCount} Pitchup.com customers
                  </translate>
                  <span class="see-breakdown" v-if="!$isDesktop">
                    <img
                      loading="lazy"
                      class="info-icon"
                      src="@/assets/images/info.svg"
                      width="19"
                      height="19"
                      alt=""
                    />
                    <translate> View breakdown </translate>
                  </span>
                </p>
              </div>
            </div>
          </template>
          <div class="ratings-breakdown-header" v-if="!$isDesktop">
            <div class="cs-overall-rating">
              <pu-rating-bubble
                :rating="overallRating"
                :rate-count="rateCount"
                :force-visibility="true"
                class="overall-rating"
              />
              <translate class="out-of" :tt="{ count: 10 }">
                out of {count}
              </translate>
            </div>
            <translate :tt="{ reviewsCount: rateCount }" tag="h4">
              Overall rating from {reviewsCount} Pitchup.com customers
            </translate>
          </div>
          <pu-rating-breakdown
            v-if="!$isDesktop"
            :breakdown="ratingBreakdown"
            :show-overall-rating="false"
            class="overall-rating-breakdown"
          />
        </pu-modal>
        <slot name="reviews-header-top-row" />
      </span>
      <pu-rating-breakdown
        v-if="$isDesktop"
        :breakdown="ratingBreakdown"
        :show-overall-rating="false"
        class="overall-rating-breakdown"
      />
    </div>
    <pu-modal
      v-if="shouldShowTheme"
      :use-portal="true"
      modal-hash="theme-breakdown"
      data-test="theme-breakdown-modal-1"
      class="themes-pu-modal"
    >
      <template #modal-trigger>
        <div class="pills-wrapper">
          <template
            v-for="(topTheme, index) in getTopThemes(themeBreakdown.themes)"
            :key="index"
          >
            <span class="pill">{{ topTheme.name }}</span>
          </template>
        </div>
      </template>
      <pu-theme-breakdown
        :campsite-name="campsiteName"
        :breakdown="themeBreakdown"
        data-test="theme-breakdown-modal-content"
      />
    </pu-modal>

    <div class="summarise">
      <span>
        <p class="would-recommend">
          {{ Number(percentages.recommend.toFixed(0)) }}%
          <translate> would recommend this site to a friend </translate>
        </p>
        <p class="stay-again">
          {{ Number(percentages.revisit.toFixed(0)) }}%
          <translate> would stay here again </translate>
        </p>
      </span>
      <campsite-reviews-summary-txt
        v-if="summaryText"
        :summary-txt="summaryText"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { Theme, RatingBreakdown, ThemeBreakdown } from '../types'

@Component
class CampsiteReviewsSummary extends Vue {
  @Prop()
  percentages: {
    recommend: number
    revisit: number
  }

  @Prop()
  rating: number

  @Prop()
  overallRating: number

  @Prop()
  rateCount: number

  @Prop()
  campsiteName: string

  @Prop()
  themeBreakdown: ThemeBreakdown

  @Prop()
  ratingBreakdown: RatingBreakdown

  @Prop({ default: null })
  summaryText: string | null

  get shouldShowTheme() {
    return this.themeBreakdown.top !== null
  }

  getTopThemes(
    themesBreakdown: Theme[],
    topN: number = 4,
    percentageThreshold: number = 15,
  ): Theme[] {
    if (!themesBreakdown.length) return []

    // Sort themes by descending mean
    const sortedThemes = [...themesBreakdown].sort((a, b) => b.mean - a.mean)

    const topMean = sortedThemes[0].mean

    // Calculate minimum acceptable mean based on threshold
    const minMean = topMean * (1 - percentageThreshold / 100)

    // Filter themes within the threshold and return top N
    return sortedThemes.filter((theme) => theme.mean >= minMean).slice(0, topN)
  }

  handleClick(event) {
    if (this.$isDesktop) {
      event.stopPropagation()
      event.preventDefault()
    }
  }
}

export default toNative(CampsiteReviewsSummary)
</script>

<style scoped>
.reviews-summary {
  pointer-events: none;
  padding: 0 1.2em;
}

.show-checkbox:checked ~ .subsection-content .reviews-summary {
  pointer-events: initial;
}

.reviews-summary :deep(p) {
  font-size: 0.85em;
  color: var(--colour-dark-blue);
}

.cell.overall p {
  font-size: 1em;
  color: var(--colour-dark-blue);
}

.cell {
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  height: 100%;
  user-select: none;
}

.row {
  justify-content: flex-start;
}

.row section.overall-rating {
  width: 100%;
}

.cell.overall {
  background: #eef6fa;
  border-radius: var(--border-radius);
  padding: 0.6em 0.6em 0.5em 1em;
  cursor: pointer;
  user-select: none;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1em;
}

.overall .overall-rating {
  font-size: 1.6em;
  margin-bottom: 0.25em;
}

.overall .overall-rating.rating-bubble:before {
  content: '';
}

.themes-pu-modal {
  display: inline-block;
  pointer-events: none;
}

.pills-wrapper {
  padding: 1em 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.7em;
}

.pill {
  background-color: #eef6fa;
  border-radius: 6em;
  display: inline-flex;
  padding: 0.4em 1em;
  font-size: 0.85em;
  color: var(--colour-dark-blue);
  cursor: pointer;
  pointer-events: all;
}

.reviews-summary :deep(.see-breakdown) {
  margin-top: 0.3em;
  font-size: 0.85em;
  text-decoration: none;
  display: none;
  align-items: center;
  gap: 0.3em;
}

.info-icon {
  filter: hue-rotate(-5deg) brightness(1.2);
}

.show-checkbox:checked
  ~ .subsection-content
  .reviews-summary
  :deep(.see-breakdown) {
  display: flex;
}

.stay-again {
  margin: 0.25em 0 0 0;
}

.would-recommend {
  margin: 0.9em 0 0 0;
}

.ratings-breakdown-header {
  padding: 2em 2.75em 0 1.4em;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
}

.ratings-breakdown-header .overall-rating {
  flex-shrink: 0;
  font-size: 1.5em;
}

.ratings-breakdown-header .overall-rating.rating-bubble:before {
  content: '';
}

.ratings-breakdown-header h4 {
  color: var(--colour-dark-blue);
  font-weight: normal;
  line-height: 1.4;
}

.ratings-breakdown-header .cs-overall-rating {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 1em;
}

.out-of {
  color: var(--colour-text-light);
  font-size: var(--small);
  margin-top: 0.25em;
  white-space: nowrap;
}

.overall-rating-breakdown {
  margin-top: 1em;
}

/* desktop & tablet */
.reviews-summary.desktop {
  padding: 0;
  font-size: 1.3em;
}

.desktop .first-row {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 3em;
}

@media (max-width: 920px) {
  .desktop .first-row {
    column-gap: 1em;
  }
}

.desktop section.overall-rating {
  width: auto;
}

.desktop .cell.overall {
  padding: 1em 1.5em;
  min-height: 4em;
  margin: 0 0 1em 0;
  /* pointer-events: none; */
  cursor: default;
}

/* .desktop .cell.overall * {
  pointer-events: none;
} */

.desktop .first-row .cell.overall {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 1em;
}

@media (max-width: 860px) {
  .desktop .first-row .cell.overall {
    flex-direction: column;
    align-items: start;
    padding: 1em;
  }
}

@media (max-width: 740px) {
  .desktop .first-row {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    margin-bottom: 1em;
  }
  .desktop .first-row .cell.overall {
    flex-direction: row;
    align-items: center;
    padding: 1.5em;
  }
}

.desktop .pills-wrapper {
  padding: 0 0 1em 0;
}

.desktop .would-recommend {
  margin: 0;
}

.desktop .overall-rating-breakdown :deep(.bar) {
  min-width: 8em;
}

.desktop .overall-rating-breakdown :deep(.bar),
.desktop .overall-rating-breakdown :deep(.bar-value) {
  height: 12px;
}

.desktop .overall-rating-breakdown :deep(th),
.desktop .overall-rating-breakdown :deep(td) {
  padding: 0;
  font-size: initial;
}

.desktop .overall-rating-breakdown :deep(th) {
  padding: 0.6em 0.6em 0.6em 0;
}

.desktop .ratings-breakdown-header {
  padding: 2em 3em 0 2.5em;
}

.desktop .ratings-breakdown-header h4 {
  font-size: 1.3em;
}

.desktop .first-row .cell.overall :deep(.rating-bubble.overall-rating) {
  margin: 0;
  flex-shrink: 1;
  font-size: 1.5em;
  z-index: 0;
}

.reviews-summary.desktop :deep(.percentage),
.reviews-summary.desktop :deep(.rating-bar .label) {
  font-size: initial;
}

@media (hover: hover) {
  .desktop .pill:hover {
    filter: brightness(0.85);
  }
}

.reviews-summary.desktop .summarise {
  display: flex;
  flex-direction: row-reverse;
  align-items: start;
  justify-content: left;
  column-gap: 2em;
  margin-top: 0.4em;
}

.reviews-summary.desktop .campsite-reviews-summary-txt {
  margin-top: 0;
}

.reviews-summary.desktop .stay-again {
  margin-top: 0.6em;
}
</style>
